import {
  type GetSessionsAnalyticsActivityArgs,
  useLazyGetSessionsAnalyticsActivityQuery,
} from '@carboncopyco/data';
import type { Resolution, ResourceType, TimePeriod } from '@carboncopyco/types';
import {
  type DropdownFilterItemContent,
  Separator,
  TextLargeParagraph,
} from '@carboncopyco/ui';
import {
  getDateFromDaysAgo,
  getNowInFormat,
  getTimezoneName,
  useDemoAnalytics,
} from '@carboncopyco/utils';
import { useCallback, useEffect, useRef, useState } from 'react';
import {
  ActivityChartFilterBar,
  ActivityChartSkeleton,
  DoubleBarChart,
  EmptyState,
  ErrorState,
  getResolutionOptions,
} from '../Charts';

type EnvironmentActivityChartCardProps = {
  organizationId: string;
  workspaceIds: string[];
  shortSessions?: boolean;
  internalSessions?: boolean;
  demoId?: string;
  timePeriodFilter?: boolean;
  location?: string;
  timePeriodValue?: TimePeriod;
  resourceTypes: (ResourceType | ResourceType[])[];
};

export const EnvironmentActivityChartCard = ({
  organizationId,
  workspaceIds,
  shortSessions = true,
  internalSessions = true,
  demoId,
  timePeriodFilter,
  location = '',
  timePeriodValue,
  resourceTypes,
}: EnvironmentActivityChartCardProps) => {
  const { trackDemoSelectTimeFilter } = useDemoAnalytics(location);
  const [resolution, setResolution] = useState<Resolution>('day');
  const [timePeriodState, setTimePeriodState] = useState<TimePeriod>('30');
  const timePeriod = timePeriodValue ?? timePeriodState;
  const stableResourceTypes = useRef(resourceTypes);

  const resolutionOptions: DropdownFilterItemContent<Resolution>[] =
    getResolutionOptions(timePeriod);

  const getFetchArgs = useCallback(
    (resourceTypes: ResourceType[]): GetSessionsAnalyticsActivityArgs => ({
      organizationId,
      shortSessions,
      internalSessions,
      workspaceIds,
      start: getDateFromDaysAgo(Number(timePeriod)),
      end: getNowInFormat('YYYY-MM-DDTHH:mm:ssZ'),
      timezone: getTimezoneName(),
      resolution,
      resourceTypes,
      demoId,
    }),
    [
      organizationId,
      shortSessions,
      internalSessions,
      workspaceIds,
      timePeriod,
      resolution,
      demoId,
    ]
  );

  const [
    getManagedPresentationOverTimeChart,
    {
      data: managedPresentationOverTimeChartData,
      isFetching: isFetchingManagedPresentationOverTimeChart,
      isError: isErrorManagedPresentationOverTimeChart,
    },
  ] = useLazyGetSessionsAnalyticsActivityQuery();

  const [
    getVisitedPresentationOverTimeChart,
    {
      data: visitedPresentationChartData,
      isFetching: isFetchingVisitedPresentationChart,
      isError: isErrorVisitedPresentationChart,
    },
  ] = useLazyGetSessionsAnalyticsActivityQuery();

  useEffect(() => {
    stableResourceTypes.current.forEach(
      (resourceType: ResourceType[] | ResourceType) => {
        if (Array.isArray(resourceType)) {
          getManagedPresentationOverTimeChart(getFetchArgs(resourceType));
          return;
        }
        resourceType === 'shared_presentation'
          ? getVisitedPresentationOverTimeChart(getFetchArgs([resourceType]))
          : getManagedPresentationOverTimeChart(getFetchArgs([resourceType]));
      }
    );
  }, [
    getFetchArgs,
    getManagedPresentationOverTimeChart,
    getVisitedPresentationOverTimeChart,
  ]);

  useEffect(() => {
    if (timePeriod === '7') {
      setResolution('day');
    }
  }, [timePeriod]);

  const isLoading =
    isFetchingManagedPresentationOverTimeChart ||
    isFetchingVisitedPresentationChart;
  const isError =
    isErrorManagedPresentationOverTimeChart || isErrorVisitedPresentationChart;
  const isEmpty =
    !isLoading &&
    !isError &&
    !managedPresentationOverTimeChartData?.results.length &&
    !visitedPresentationChartData?.results.length;

  return (
    <div className="flex flex-col w-full h-full bg-ds-accent3-100 rounded-lg border border-ds-natural-150">
      <div className="flex justify-between items-center h-ds-15 px-6 shrink-0">
        <TextLargeParagraph
          label="Activity over time"
          color="text-ds-natural-600"
        />
        <ActivityChartFilterBar
          setResolution={(resolutionItem) => {
            trackDemoSelectTimeFilter({
              period: timePeriod,
              resolution: resolutionItem,
            });
            setResolution(resolutionItem);
          }}
          resolution={resolution}
          resolutionOptions={resolutionOptions}
          {...(timePeriodFilter
            ? {
                timePeriod: timePeriodState,
                setTimePeriod: setTimePeriodState,
              }
            : {})}
        />
      </div>
      <div className="w-full">
        <Separator horizontal />
      </div>
      <div className="h-[225px]">
        {isLoading ? (
          <div className="flex justify-center items-center h-full">
            <ActivityChartSkeleton />
          </div>
        ) : isError ? (
          <div className="h-full flex items-center justify-center">
            <ErrorState />
          </div>
        ) : isEmpty ? (
          <div className="flex justify-center items-center h-full">
            <EmptyState />
          </div>
        ) : (
          visitedPresentationChartData &&
          managedPresentationOverTimeChartData && (
            <div className="m-4">
              <DoubleBarChart
                timePeriodValue={timePeriod}
                visitedData={visitedPresentationChartData}
                presentedData={managedPresentationOverTimeChartData}
              />
            </div>
          )
        )}
      </div>
    </div>
  );
};
